import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Icon, SLOT_ICON, Chev } from "../shared/icons";
import DetectionOverlay, { SCENE_SETS } from "./DetectionOverlay";
import {
  SLOTS, SLOT_LABEL, SLOT_MEDIA, STATUS_LABEL,
  VERTICALS, FILES, DASHBOARDS, CAPTURE, INSIDE, slotHasContent, fileHasContent,
  orderedSlots, isPdf,
} from "../shared/data";

/*
 * Camcom Next screens — the same eight screens and the same catalogue as Camcom One,
 * rendered in the expressive dark register. Strings are co_* keys; `brand` is a literal.
 */

export const findVertical = (vk) => VERTICALS.find((v) => v.key === vk);
export const parseId = (id) => {
  const [vk, uk, slot] = id.split("|");
  const v = findVertical(vk);
  return { v, u: v && v.ucs.find((x) => x.key === uk), slot };
};

const countUC = (u) => {
  let a = 0, r = 0;
  SLOTS.forEach((k) => { const s = u.d[k].s; if (s !== "na") { a++; if (s === "ready") r++; } });
  return { a, r };
};
const countVertical = (v) =>
  v.ucs.reduce((acc, u) => { const c = countUC(u); return { a: acc.a + c.a, r: acc.r + c.r }; }, { a: 0, r: 0 });

const nf = (n) =>
  typeof n === "number" && n % 1 !== 0 ? n.toFixed(1) : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/* staggered entrance — the register asks for motion on every step */
const stagger = { animate: { transition: { staggerChildren: 0.05 } } };
const rise = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] } },
};

const Meter = ({ c }) => {
  const { t } = useTranslation();
  const p = c.a ? Math.round((100 * c.r) / c.a) : 0;
  return (
    <div className="cn-meter">
      <div className="cn-track">
        <i className="rdy" style={{ width: `${p}%` }} />
        <i className="wip" style={{ width: `${100 - p}%` }} />
      </div>
      <div className="cn-mcap"><b>{c.r}</b> {t("co_of")} {c.a} {t("co_in_kit")}</div>
    </div>
  );
};

const Notes = ({ items }) => (
  <div className="cn-notes">
    {items.map(([b, s], i) => <span key={i}>{b && <b>{b}</b>} {s}</span>)}
  </div>
);

const Empty = ({ title, body }) => (
  <div className="cn-empty"><div className="big">{title}</div><p>{body}</p></div>
);

/* pointer-tracked glow on cards */
const glow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

/* ── deliverable row ─────────────────────────────── */

export const Row = ({ v, u, slot, present, has, onAct }) => {
  const { t } = useTranslation();
  const it = u.d[slot];
  const s = it.s;
  const id = `${v.key}|${u.key}|${slot}`;
  const Ic = SLOT_ICON[slot];

  if (s === "na") {
    if (present) return null;
    return (
      <div className="cn-row off">
        <span className="cn-ic"><Ic /></span>
        <span className="cn-tx"><h4>{t(SLOT_LABEL[slot])}</h4></span>
        <span className="cn-act na">{t("co_act_na")}</span>
      </div>
    );
  }

  const files = FILES[`${v.key}|${u.key}|${slot}`];
  /* `live` is the visual treatment (status-driven); `content` is whether the button does
     anything at all. A row with nothing behind it is disabled, not merely dimmed. */
  const content = slotHasContent(v, u, slot);
  const live = content && (present || s === "ready");
  const verb = slot === "demo" ? t("co_act_launch") : slot === "dash" ? t("co_act_view") : t("co_act_open");
  const sub =
    slot === "demo" ? (it.capture ? t("co_sub_capture_ext") : t("co_sub_newtab"))
    : slot === "dash" ? t("co_sub_livedash")
    : files ? `${files.length} ${files.length > 1 ? t("co_file_many") : t("co_file_one")}`
    : it.n ? t(it.n) : t(SLOT_MEDIA[slot]);

  return (
    <motion.button variants={rise} className={`cn-row${live ? "" : " off"}`}
      disabled={!content} aria-disabled={!content} onClick={() => onAct(id)}>
      <span className={`cn-ic${slot === "vid" ? " v" : ""}`}><Ic /></span>
      <span className="cn-tx">
        <h4>{t(SLOT_LABEL[slot])}</h4>
        <span className="cn-meta">
          {!present && <span className={`cn-chip ${s}`}>{t(STATUS_LABEL[s])}</span>}
          <span>{sub}</span>
        </span>
      </span>
      {has(id) && <span className="cn-tick">✓</span>}
      {live
        ? <span className="cn-act">{slot === "demo" ? <Icon.Ext /> : <Icon.Open />}{verb}</span>
        : content
          ? <span className="cn-act ghost"><Icon.Eye />{t("co_act_preview")}</span>
          : <span className="cn-act na">{t("co_act_empty")}</span>}
    </motion.button>
  );
};

/* ── home ────────────────────────────────────────── */

export const Home = ({ present, onGo, onPickScene }) => {
  const { t } = useTranslation();
  const total = VERTICALS.reduce((acc, v) => {
    const c = countVertical(v); return { a: acc.a + c.a, r: acc.r + c.r };
  }, { a: 0, r: 0 });

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise} className="cn-hero">
        <div className="cn-herotext">
          <span className="cn-kicker"><b>◆</b> {t("co_sect_verticals")} · {VERTICALS.length}</span>
          <h1 className="cn-h1">
            {t("co_home_h1_a")}<br /><em>{t("co_home_h1_b")}</em>
          </h1>
          <p className="cn-lead">{t("co_home_lead")}</p>
        </div>
        <DetectionOverlay onPick={onPickScene} />
      </motion.div>

      <div className="cn-sect">{t("co_sect_verticals")}</div>
      <motion.div variants={stagger} className="cn-grid">
        {VERTICALS.map((v) => {
          const c = countVertical(v);
          return (
            <motion.button variants={rise} key={v.key} className="cn-card"
              onMouseMove={glow} onClick={() => onGo(v.key)}>
              <div className="cn-top">
                <div style={{ minWidth: 0 }}>
                  {/* brand lockup is never translated */}
                  <span className="cn-brandtag">{v.brand}</span>
                  <h3>{t(v.name)}</h3>
                  <p className="cn-sub">{v.sub ? t(v.sub) : v.ucs.map((u) => t(u.short)).join(" · ")}</p>
                </div>
                <span className="cn-arrow"><Chev /></span>
              </div>
              {!present && <Meter c={c} />}
            </motion.button>
          );
        })}
        <motion.button variants={rise} className="cn-card wide" onMouseMove={glow} onClick={() => onGo("inside")}>
          <div className="cn-top">
            <div>
              <span className="cn-brandtag">{t("co_inside_kicker")}</span>
              <h3>{t("co_inside_name")}</h3>
              <p className="cn-sub" style={{ fontFamily: "var(--nm)" }}>{t("co_inside_sub")}</p>
            </div>
            <span className="cn-arrow"><Chev /></span>
          </div>
        </motion.button>
      </motion.div>

      {!present && (
        <div className="cn-note">
          <b>{total.r} {t("co_of")} {total.a} {t("co_flag_a")}</b> {t("co_flag_b")}
          <br /><br /><b>{t("co_flag_c")}</b> {t("co_flag_d")}
        </div>
      )}

      <Notes items={[
        [t("co_note_two_taps_b"), t("co_note_two_taps")],
        [t("co_note_six_slots_b"), t("co_note_six_slots")],
      ]} />
    </motion.div>
  );
};

/* ── vertical ────────────────────────────────────── */

export const Vertical = ({ vk, activeUC, setActiveUC, present, has, onAct }) => {
  const { t } = useTranslation();
  const v = findVertical(vk);
  if (!v) return null;
  const u = v.ucs.find((x) => x.key === activeUC) || v.ucs[0];
  const c = countUC(u);
  const sceneSet = SCENE_SETS[`${v.key}|${u.key}`];

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      {/* a use case with its own scene set gets the same split header as the home hero */}
      <motion.div variants={rise} className={sceneSet ? "cn-hero" : undefined}>
        <div className={sceneSet ? "cn-herotext" : undefined}>
          <span className="cn-kicker">{v.brand}</span>
          <h2 className="cn-h2">{t(v.name)}</h2>
          <p className="cn-lead">{t(v.line)}</p>
        </div>
        {sceneSet && <DetectionOverlay key={`${vk}-${u.key}`} scenes={sceneSet} />}
      </motion.div>

      {v.ucs.length > 1 && (
        <motion.div variants={rise} className="cn-ucbar">
          {v.ucs.map((x) => {
            const cc = countUC(x);
            return (
              <button key={x.key} className="cn-uc" aria-current={x.key === u.key}
                onClick={() => setActiveUC(x.key)}>
                {t(x.short)}{!present && <span className="n">{cc.r}/{cc.a}</span>}
              </button>
            );
          })}
        </motion.div>
      )}

      <div className="cn-sect">{v.ucs.length > 1 ? t(u.name) : t("co_deliverables")}</div>
      {!present && <Meter c={c} />}
      <motion.div variants={stagger} className="cn-rows" style={{ marginTop: 14 }}>
        {orderedSlots(v, u).map((k) => (
          <Row key={k} v={v} u={u} slot={k} present={present} has={has} onAct={onAct} />
        ))}
      </motion.div>
      <Notes items={[[t("co_note_order_b"), t("co_note_order")]]} />
    </motion.div>
  );
};

/* ── files ───────────────────────────────────────── */

export const Files = ({ id, has, onOpenFile, onAddAll }) => {
  const { t } = useTranslation();
  const { v, u, slot } = parseId(id);
  const files = FILES[id] || [];
  const realCount = files.filter(fileHasContent).length;
  const status = u.d[slot].s;
  const Ic = SLOT_ICON[slot];

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker">{v.brand}</span>
        <h2 className="cn-h2">{t(SLOT_LABEL[slot])}</h2>
        <p className="cn-lead">
          {t(u.name)} · {files.length
            ? `${files.length} ${files.length !== 1 ? t("co_file_many") : t("co_file_one")}`
            : t("co_files_empty_c")}
        </p>
      </motion.div>

      {!files.length ? (
        <>
          <div className="cn-note">{t("co_folder_preview")} · {t(STATUS_LABEL[status]).toLowerCase()}</div>
          <Empty title={t("co_files_none_t")} body={u.d[slot].n ? t(u.d[slot].n) : t("co_files_none_b")} />
        </>
      ) : (
        <>
          <div className="cn-sect">{t("co_sect_files")}</div>
          <motion.div variants={stagger} className="cn-rows">
            {files.map((f, i) => {
              const fid = `${id}#${i}`;
              const isVid = slot === "vid";
              return (
                <motion.button variants={rise} key={fid}
                  className={`cn-row${fileHasContent(f) ? "" : " off"}`}
                  disabled={!fileHasContent(f)} aria-disabled={!fileHasContent(f)}
                  onClick={() => onOpenFile(fid)}>
                  <span className={`cn-ic${isVid ? " v" : ""}`}>{isVid ? <Icon.Play /> : <Ic />}</span>
                  <span className="cn-tx">
                    <h4>{t(f.t)}</h4>
                    <span className="cn-meta"><span>{t(f.m)}</span></span>
                  </span>
                  {has(fid) && <span className="cn-tick">✓</span>}
                  {fileHasContent(f)
                    ? <span className="cn-act"><Icon.Open />{t("co_act_open")}</span>
                    : <span className="cn-act na">{t("co_act_empty")}</span>}
                </motion.button>
              );
            })}
          </motion.div>
          {realCount > 0 && (
            <button className="cn-btn" onClick={() => onAddAll(id)}>
              <Icon.Plus />{t("co_addall")} {realCount} {t("co_to_share")}
            </button>
          )}
        </>
      )}
      <Notes items={[[t("co_note_folder_b"), t("co_note_folder")]]} />
    </motion.div>
  );
};

/* ── assets ──────────────────────────────────────── */

/* Every real asset in the kit, flat, in one list. The rest of the app is organised by vertical
   and use case, which is right for a structured pitch and wrong when someone asks "can you show
   me the tollway deck" mid-conversation. This is the shortcut: two groups, every row opens the
   same sheet the folders use, and the vertical it belongs to is shown so the list stays
   readable at 36 rows. Placeholder entries never appear — only things with a real src. */
export const Assets = ({ has, onOpenFile, onOpenVideo }) => {
  const { t } = useTranslation();

  const groups = React.useMemo(() => {
    const vids = [];
    const docs = [];
    VERTICALS.forEach((v) => v.ucs.forEach((u) => SLOTS.forEach((slot) => {
      const id = `${v.key}|${u.key}|${slot}`;
      (FILES[id] || []).forEach((f, i) => {
        if (!fileHasContent(f)) return;
        (isPdf(f.src) ? docs : vids).push({ fid: `${id}#${i}`, f, v, u, slot });
      });
    })));
    return [
      { key: "vid", label: t("co_assets_videos"), items: vids },
      { key: "doc", label: t("co_assets_docs"), items: docs },
    ].filter((g) => g.items.length);
  }, [t]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker"><b>◆</b> {t("co_assets_count", { n: total })}</span>
        <h2 className="cn-h2">{t("co_assets_h1")}</h2>
        <p className="cn-lead">{t("co_assets_lead")}</p>
      </motion.div>

      {!total && <Empty title={t("co_assets_empty_t")} body={t("co_assets_empty_b")} />}

      {groups.map((g) => (
        <div key={g.key}>
          <div className="cn-sect">{g.label} · {g.items.length}</div>
          <motion.div variants={stagger} className="cn-rows">
            {g.items.map(({ fid, f, v, u, slot }) => (
              <motion.button variants={rise} key={fid} className="cn-row"
                onClick={() => (slot === "vid" || !isPdf(f.src)
                  ? onOpenVideo(fid)
                  : onOpenFile(fid))}>
                <span className={`cn-ic${slot === "vid" ? " v" : ""}`}>
                  {slot === "vid" ? <Icon.Play /> : React.createElement(SLOT_ICON[slot])}
                </span>
                <span className="cn-tx">
                  <h4>{t(f.t)}</h4>
                  <span className="cn-meta">
                    <span className="cn-asrc">{t(v.name)} · {t(u.name)}</span>
                    <span>{t(f.m)}</span>
                  </span>
                </span>
                {has(fid) && <span className="cn-tick">✓</span>}
                <span className="cn-act"><Icon.Open />{t("co_act_open")}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      ))}
    </motion.div>
  );
};

/* ── dashboard ───────────────────────────────────── */

export const Dashboard = ({ id, has, onAddDash }) => {
  const { t } = useTranslation();
  const { v, u } = parseId(id);
  const d = DASHBOARDS[`${v.key}|${u.key}`];
  if (!d) return <Empty title={t("co_dash_none_t")} body={t("co_dash_none_b")} />;

  const mx = Math.max(...d.trend.values);
  const Breakdown = ({ o }) => {
    const m = Math.max(...o.rows.map((r) => r.v));
    return (
      <motion.div variants={rise} className="cn-panel">
        <h4>{t(o.title)}</h4>
        <p className="ph">{t(o.sub)}</p>
        {o.rows.map((r, i) => (
          <div className="cn-hbar" key={i}>
            <span className="n">{t(r.n)}</span>
            <span className="t"><i style={{ width: `${Math.round((100 * r.v) / m)}%` }} /></span>
            <span className="v">{nf(r.v)}</span>
          </div>
        ))}
      </motion.div>
    );
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker">{v.brand}</span>
        <h2 className="cn-h2">{t("co_dash_h")}</h2>
        <p className="cn-lead">{t(u.name)}</p>
      </motion.div>
      <div className="cn-note">
        {t("co_dash_illustrative")}
        {u.d.dash.s !== "ready" && ` · ${t("co_dash_preview")}, ${t(STATUS_LABEL[u.d.dash.s]).toLowerCase()}`}
      </div>

      <motion.div variants={stagger} className="cn-kpis">
        {d.kpis.map((x, i) => (
          <motion.div variants={rise} className="cn-kpi" key={i}>
            <b>{t(x.v)}</b><span className="l">{t(x.l)}</span>
            {x.d && <span className="d">{t(x.d)}</span>}
          </motion.div>
        ))}
      </motion.div>

      <motion.div variants={rise} className="cn-panel">
        <h4>{t(d.trend.title)}</h4>
        <p className="ph">{t(d.trend.sub)} · {t("co_latest")} {nf(d.trend.values[d.trend.values.length - 1])}</p>
        <div className="cn-vbars">
          {d.trend.values.map((x, i) => (
            <div className={`col${i === d.trend.values.length - 1 ? " last" : ""}`} key={i}>
              <motion.span className="b" initial={{ height: 0 }}
                animate={{ height: `${Math.round((100 * x) / mx)}%` }}
                transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.2, 0.8, 0.2, 1] }} />
            </div>
          ))}
        </div>
        <div className="cn-vlabels">{d.trend.labels.map((l, i) => <span key={i}>{t(l)}</span>)}</div>
      </motion.div>

      <Breakdown o={d.breakdowns[0]} />
      <Breakdown o={d.breakdowns[1]} />

      <button className="cn-btn" onClick={() => onAddDash(id)}>
        {has(id) ? `${t("co_added")} ✓` : <><Icon.Plus />{t("co_add_dash")}</>}
      </button>
      <Notes items={[[t("co_note_dash_b"), t("co_note_dash")]]} />
    </motion.div>
  );
};

/* ── capture ─────────────────────────────────────── */

export const Capture = ({ id, present, has, onLaunch, onAddLink }) => {
  const { t } = useTranslation();
  const { v, u } = parseId(id);
  const ready = u.d.demo.s === "ready" || present;
  const icons = [<Icon.Cam key="c" />, <Icon.Vcam key="v" />];

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker">{v.brand}</span>
        <h2 className="cn-h2">{t("co_cap_h")}</h2>
        <p className="cn-lead">{t(u.name)} — {t("co_cap_intro")}</p>
      </motion.div>
      <motion.div variants={stagger} className="cn-caps">
        {CAPTURE.map((x, i) => (
          <motion.button variants={rise} className="cn-cap" key={i} disabled={!ready}
            onClick={ready ? () => onLaunch(i) : undefined}>
            <span className="capic">{icons[i]}</span>
            <b>{t(x.t)}</b>
            <span>{t(x.b)}</span>
            <span className="cn-act" style={{ marginTop: 18, display: "inline-flex" }}>
              {ready ? <><Icon.Ext />{t("co_act_launch")}</> : t("co_cap_planned")}
            </span>
          </motion.button>
        ))}
      </motion.div>
      {ready && (
        <button className="cn-btn" onClick={() => onAddLink(id)}>
          {has(id) ? `${t("co_added")} ✓` : <><Icon.Plus />{t("co_add_demo")}</>}
        </button>
      )}
      <Notes items={[[t("co_note_cap_b"), t("co_note_cap")]]} />
    </motion.div>
  );
};

/* ── ready now ───────────────────────────────────── */

export const ReadyNow = ({ present, has, onAct }) => {
  const { t } = useTranslation();
  let n = 0;
  const groups = VERTICALS.map((v) => {
    const items = [];
    /* a slot only belongs on "Ready now" if it can actually be opened — listing rows that
       render disabled would promise something the screen cannot deliver */
    v.ucs.forEach((u) => SLOTS.forEach((k) => {
      if (u.d[k].s === "ready" && slotHasContent(v, u, k)) items.push({ u, k });
    }));
    n += items.length;
    return items.length ? { v, items } : null;
  }).filter(Boolean);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker"><b>◆</b> {n} {t("co_ready_items")}</span>
        <h2 className="cn-h2">{t("co_ready_h1")}</h2>
        <p className="cn-lead">{t("co_ready_lead")}</p>
      </motion.div>
      {!n && <Empty title={t("co_ready_empty_t")} body={t("co_ready_empty_b")} />}
      {groups.map(({ v, items }) => (
        <div key={v.key}>
          <div className="cn-sect">{t(v.name)}</div>
          <motion.div variants={stagger} className="cn-rows">
            {items.map(({ u, k }) => (
              <Row key={`${v.key}|${u.key}|${k}`} v={v} u={u} slot={k}
                present={present} has={has} onAct={onAct} />
            ))}
          </motion.div>
        </div>
      ))}
      {!!n && <Notes items={[[`${n} ${t("co_ready_items")}.`, t("co_ready_note")]]} />}
    </motion.div>
  );
};

/* ── share ───────────────────────────────────────── */

export const Share = ({ picked, onRemove, onClear, email, onEmail, note, onNote, sent, onSend }) => {
  const { t } = useTranslation();
  const count = `${picked.length} ${picked.length > 1 ? t("co_share_items") : t("co_share_item")}`;
  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker"><b>◆</b> {picked.length}</span>
        <h2 className="cn-h2">{t("co_share_h1")}</h2>
        <p className="cn-lead">{t("co_share_lead")}</p>
      </motion.div>

      {!picked.length ? (
        <Empty title={t("co_share_empty_t")} body={t("co_share_empty_b")} />
      ) : (
        <>
          <motion.div variants={stagger} className="cn-rows" style={{ marginTop: 26 }}>
            {picked.map((p) => {
              const Ic = SLOT_ICON[p.k] || Icon.Broc;
              return (
                <motion.div variants={rise} className="cn-row" key={p.id}>
                  <span className={`cn-ic${p.k === "vid" ? " v" : ""}`}><Ic /></span>
                  <span className="cn-tx">
                    <h4>{p.tKey ? t(p.tKey) : p.t}</h4>
                    <span className="cn-meta"><span>{p.s}</span></span>
                  </span>
                  <button className="cn-remove" onClick={() => onRemove(p.id)}>{t("co_remove")}</button>
                </motion.div>
              );
            })}
          </motion.div>
          <button className="cn-clear" onClick={onClear}>{t("co_share_clear")}</button>

          <div className="cn-sect">{t("co_share_sendto")}</div>
          <input className="cn-field" type="email" value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder={t("co_share_email")} aria-label={t("co_share_email")} />
          <input className="cn-field" type="text" value={note}
            onChange={(e) => onNote(e.target.value)}
            placeholder={t("co_share_note_ph")} aria-label={t("co_share_note_ph")} />
          <button className="cn-btn" onClick={onSend}>
            {t("co_share_send")} {count}<span aria-hidden="true">→</span>
          </button>
          {/* Sending is NOT wired (F10) — state what would happen, never imply delivery. */}
          {sent && (
            <div className="cn-note">
              {email.trim()
                ? t("co_share_sent_to", { count, email: email.trim() })
                : t("co_share_sent_norecipient")}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

/* ── inside ──────────────────────────────────────── */

export const Inside = () => {
  const { t } = useTranslation();
  return (
    <motion.div variants={stagger} initial="initial" animate="animate">
      <motion.div variants={rise}>
        <span className="cn-kicker">{t("co_inside_kicker")}</span>
        <h2 className="cn-h2">{t("co_inside_name")}</h2>
        <p className="cn-lead">{t("co_inside_line")}</p>
      </motion.div>
      <div className="cn-sect">{t("co_sect_inside_what")}</div>
      <motion.div variants={stagger} className="cn-rows">
        {INSIDE.map((r, i) => (
          <motion.div variants={rise} className="cn-row off" key={i}>
            <span className="cn-ic"><Icon.Broc /></span>
            <span className="cn-tx">
              <h4>{t(r.h)}</h4>
              <span className="cn-meta"><span>{t(r.s)}</span></span>
            </span>
            <span className="cn-chip">{t(r.tag)}</span>
          </motion.div>
        ))}
      </motion.div>
      <Notes items={[[t("co_note_kill_b"), t("co_note_kill")]]} />
    </motion.div>
  );
};
